import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { z } from "zod";
import AppButton from "../../components/AppButton";
import { LOGIN_STATUS, useAuth } from "../../context/AuthContext";
import { ROUTES } from "../config";
import AuthFlowTitle from "./components/AuthFlowTitle";
import FormLoginInput from "./components/FormLoginInput";
import MFAForm from "./components/MFAForm";
import { useSearchParam } from "react-use";

// Zod schema for login form validation
const loginSchema = z.object({
    username: z.string().min(1, "User name is required"),
    password: z.string().min(8, "Password must be at least 8 characters"),
});

type LoginFormData = z.infer<typeof loginSchema>;

const Login = () => {
    const { login, fetchAndSetUser } = useAuth();
    const sessionExpired = useSearchParam("sessionExpired") === "1";
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [step, setStep] = useState<1 | 2>(1);
    const navigate = useNavigate();

    const {
        register,
        handleSubmit,
        formState: { errors: formErrors },
        watch,
    } = useForm<LoginFormData>({
        resolver: zodResolver(loginSchema),
    });

    const [watchUsername, watchPassword] = watch(["username", "password"]);

    const submitFirstStep = async (data: LoginFormData) => {
        setSubmitting(true);
        setError("");
        const errorMsg = "Error Logging In";
        try {
            const loginStatus = await login(data.username, data.password);
            if (loginStatus.status === LOGIN_STATUS.ERROR) {
                if (loginStatus.error_code === "reset-password-required") {
                    navigate(`/${ROUTES.RESET_PASSWORD.path}`);
                    return;
                }
                throw new Error(loginStatus.error || errorMsg);
            } else if (loginStatus.status === LOGIN_STATUS.OTP) {
                setStep(2);
            }
        } catch (error) {
            setError(error instanceof Error ? error.message : errorMsg);
        } finally {
            setSubmitting(false);
        }
    };

    return step === 1 ? (
        <form
            className="h-full flex flex-col gap-5"
            onSubmit={handleSubmit(submitFirstStep)}
        >
            <AuthFlowTitle>Log In</AuthFlowTitle>
            {sessionExpired && (
                <div className="text-red-error text-sm">
                    Your session has expired. Please log in again.
                </div>
            )}
            <FormLoginInput
                id="username"
                label="User Name"
                {...register("username")}
                placeholder="Enter your user name"
                autoComplete={false}
                error={formErrors.username?.message}
            />
            <div className="flex flex-col">
                <FormLoginInput
                    id="password"
                    label="Password"
                    {...register("password")}
                    type="password"
                    placeholder="Enter your password"
                    autoComplete={false}
                    error={formErrors.password?.message}
                />
                <Link
                    to={`/${ROUTES.RESET_PASSWORD.path}`}
                    className="block mt-2 text-gray-medium text-sm hover:underline"
                >
                    Forgot password?
                </Link>
            </div>
            <div className="mt-auto flex flex-col gap-2">
                {error && <div className="text-red-error">{error}</div>}
                <AppButton type="submit" loading={submitting}>
                    Continue
                </AppButton>
            </div>
        </form>
    ) : (
        <MFAForm
            username={watchUsername}
            onSuccess={async () => {
                await fetchAndSetUser();
                setTimeout(() => {
                    navigate(`/${ROUTES.OPEN_TEST.path}`);
                }, 100);
            }}
            sendCodeAgain={async () => {
                await login(watchUsername, watchPassword);
            }}
            codeSentVia="sms"
        />
    );
};

export default Login;
